import React, { useState, useEffect } from 'react';
import { PriceOptionsEditor } from '../components/PriceOptionsEditor';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Package, Plus, Edit, Trash2, ShieldCheck, AlertCircle, Tag, FileSpreadsheet, Download, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SAR = () => <span style={{ fontFamily: 'inherit' }}>﷼</span>;

const FIELD_LABELS = {
  country: 'الدولة',
  customer_name: 'اسم العميل',
  customer_phone: 'رقم التواصل',
  activity_type: 'نوع النشاط',
};

const blankForm = { name: '', price: '', field_rule_id: '', allowed_roles: [], kind: 'primary', price_options_enabled: false, price_options: [] };

const ROLE_OPTIONS = [
  { value: 'admin', label: 'المدير' },
  { value: 'supervisor', label: 'المشرف' },
  { value: 'employee', label: 'الموظف' },
];

const renderAllowedRolesLabel = (roles) => {
  if (!roles || roles.length === 0) return 'الجميع';
  return roles.map((r) => ROLE_OPTIONS.find((o) => o.value === r)?.label || r).join('، ');
};

export default function SuperProducts() {
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API_URL}/api/super/products`),
        axios.get(`${API_URL}/api/super/field-rules`),
      ]);
      setProducts(p.data || []);
      setRules(r.data || []);
    } catch (e) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const ruleById = (id) => rules.find((r) => r.id === id);

  // Rules that target this product via `master_ids` (new model — replaces the
  // legacy keyword-name matching).
  const rulesForProduct = (product) => {
    const mid = product?.master_id;
    if (!mid) return [];
    return rules.filter((r) => r.active && Array.isArray(r.master_ids) && r.master_ids.includes(mid));
  };

  const openAdd = () => {
    setEditing(null);
    setForm(blankForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.master_id || p.id);
    setForm({
      name: p.name || '',
      price: p.price?.toString() || '',
      field_rule_id: p.field_rule_id || '',
      allowed_roles: Array.isArray(p.allowed_roles) ? p.allowed_roles : [],
      kind: p.kind === 'additional' ? 'additional' : 'primary',
      price_options_enabled: !!p.price_options_enabled,
      price_options: (p.price_options || []).map((o) => ({ ...o, price: (o.price ?? 0).toString() })),
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('اسم المنتج مطلوب');
      return;
    }
    const payload = {
      name: form.name.trim(),
      price: parseFloat(form.price) || 0,
      field_rule_id: form.field_rule_id || null,
      allowed_roles: form.allowed_roles || [],
      kind: form.kind === 'additional' ? 'additional' : 'primary',
      price_options_enabled: !!form.price_options_enabled,
      price_options: (form.price_options || [])
        .filter((o) => (o.label || '').trim())
        .map((o) => ({ ...(String(o.id).startsWith('tmp-') ? {} : { id: o.id }), label: o.label.trim(), price: parseFloat(o.price) || 0 })),
    };
    if (payload.price_options_enabled && payload.price_options.length === 0) {
      toast.error('أضف خياراً واحداً على الأقل أو أوقف خيارات الأسعار');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/super/products/${editing}`, payload);
        toast.success('تم تحديث المنتج في كل الفروع');
      } else {
        await axios.post(`${API_URL}/api/super/products`, payload);
        toast.success('تم إنشاء المنتج في كل الفروع');
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`حذف المنتج «${p.name}» من جميع الفروع؟`)) return;
    try {
      await axios.delete(`${API_URL}/api/super/products/${p.master_id || p.id}`);
      toast.success('تم الحذف من جميع الفروع');
      fetchAll();
    } catch (err) {
      toast.error('فشل الحذف');
    }
  };

  const handleToggleHidden = async (p) => {
    const willHide = !p.hidden;
    const action = willHide ? 'إخفاء' : 'إظهار';
    if (!window.confirm(`${action} المنتج «${p.name}»؟ ${willHide ? 'لن يظهر في قوائم تسجيل المبيعات بعد الإخفاء.' : ''}`)) return;
    try {
      await axios.put(`${API_URL}/api/super/products/${p.master_id || p.id}`, { hidden: willHide });
      toast.success(willHide ? 'تم إخفاء المنتج' : 'تم إظهار المنتج');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `فشل ${action}`);
    }
  };

  // Reorder products (the display order shown to employees on the sales form).
  // Optimistic update — the backend call persists the new order; on failure we refetch.
  const [reordering, setReordering] = useState(false);
  const persistOrder = async (orderedList) => {
    setReordering(true);
    try {
      const master_ids = orderedList.map((p) => p.master_id || p.id);
      await axios.post(`${API_URL}/api/super/products/reorder`, { master_ids });
    } catch (err) {
      toast.error('فشل حفظ الترتيب');
      fetchAll();
    } finally {
      setReordering(false);
    }
  };

  const moveProduct = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= products.length) return;
    const next = [...products];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setProducts(next);
    persistOrder(next);
  };

  // ── Sales export (Excel) ───────────────────────────────
  const [exportOpen, setExportOpen] = useState(false);
  const [exportProduct, setExportProduct] = useState('all');
  const [exportPeriod, setExportPeriod] = useState('monthly');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleExportSales = async () => {
    if (exportPeriod === 'custom') {
      if (!exportStartDate || !exportEndDate) {
        toast.error('حدد تاريخ البداية وتاريخ النهاية');
        return;
      }
      if (exportStartDate > exportEndDate) {
        toast.error('تاريخ البداية يجب أن يسبق تاريخ النهاية');
        return;
      }
    }
    setDownloading(true);
    try {
      const params = { period: exportPeriod };
      if (exportProduct && exportProduct !== 'all') params.product_name = exportProduct;
      if (exportPeriod === 'custom') {
        params.start_date = exportStartDate;
        params.end_date = exportEndDate;
      }
      const r = await axios.get(`${API_URL}/api/super/exports/sales/excel`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (exportProduct === 'all' ? 'all' : exportProduct).replace(/\s+/g, '_');
      const suffix = exportPeriod === 'custom' ? `${exportStartDate}_${exportEndDate}` : exportPeriod;
      a.download = `sales_${safe}_${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('تم تنزيل الملف');
      setExportOpen(false);
    } catch (e) {
      toast.error('فشل التصدير');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="super-products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <Package className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-foreground">المنتجات</h1>
            <p className="text-sm text-foreground/60">
              إدارة موحّدة لقائمة المنتجات — أي تعديل يُطبَّق على كل الفروع تلقائياً.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setExportOpen(true)}
            variant="outline"
            className="gap-2"
            data-testid="export-sales-button"
          >
            <FileSpreadsheet className="h-4 w-4" />
            تصدير المبيعات
          </Button>
          <Button onClick={openAdd} className="gap-2" data-testid="add-product-button">
            <Plus className="h-4 w-4" />
            إضافة منتج
          </Button>
        </div>
      </div>

      <Card className="p-4 bg-brand_blue/5 border-brand_blue/20 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-brand_blue mt-0.5 flex-shrink-0" />
        <div className="text-sm text-foreground/80 leading-relaxed">
          <p>
            <span className="font-bold">قائمة موحّدة:</span> كل منتج تنشئه هنا يُضاف فوراً إلى كل
            الفروع. لا يستطيع مديرو الفروع إضافة أو تعديل أو حذف المنتجات — يعرضونها فقط.
          </p>
          <p className="mt-1">
            <span className="font-bold">ترتيب العرض للموظف:</span> استخدم أسهم{' '}
            <ChevronUp className="inline h-3 w-3" />/<ChevronDown className="inline h-3 w-3" />{' '}
            في عمود «ترتيب» لتغيير ترتيب المنتجات كما تظهر للموظف عند تسجيل المبيعات. الترتيب يُحفظ تلقائياً.
          </p>
          <p className="mt-1">
            <span className="font-bold">الحقول الإلزامية:</span> اربط المنتج بقاعدة من{' '}
            <a href="/super/field-rules" className="font-bold text-brand_blue underline">
              قواعد الحقول الإلزامية
            </a>{' '}
            أو اختر المنتج من داخل صفحة القواعد لتفعيل الحقول الإلزامية عليه.
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="super-products-table">
            <thead className="bg-accent/40 border-b border-border">
              <tr>
                <th className="text-right py-3 px-3 text-sm font-bold w-16">ترتيب</th>
                <th className="text-right py-3 px-4 text-sm font-bold">اسم المنتج</th>
                <th className="text-right py-3 px-4 text-sm font-bold">النوع</th>
                <th className="text-right py-3 px-4 text-sm font-bold">السعر</th>
                <th className="text-right py-3 px-4 text-sm font-bold">قاعدة الحقول</th>
                <th className="text-right py-3 px-4 text-sm font-bold">حقول إلزامية مستنتجة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">صلاحية الاستخدام</th>
                <th className="text-right py-3 px-4 text-sm font-bold">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="py-12 text-center text-foreground/50">جاري التحميل...</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan="8" className="py-12 text-center text-foreground/50">لا توجد منتجات بعد</td></tr>
              ) : (
                products.map((p, idx) => {
                  const rule = ruleById(p.field_rule_id);
                  const targeted = rulesForProduct(p).filter((r) => r.id !== p.field_rule_id);
                  // Union of fields
                  const fieldKeys = new Set([
                    ...((rule?.required_fields) || []),
                    ...targeted.flatMap((r) => r.required_fields || []),
                  ]);
                  // Display label for the rule chip (if any rule applies to this product)
                  const ruleChip = rule || targeted[0];
                  const isFirst = idx === 0;
                  const isLast = idx === products.length - 1;
                  return (
                    <tr key={p.master_id || p.id} className={`border-b border-border hover:bg-accent/30 ${p.hidden ? 'opacity-50' : ''}`} data-testid={`product-row-${p.master_id || p.id}`}>
                      <td className="py-3 px-2 align-middle">
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded bg-accent text-foreground/60 text-[11px] font-bold font-numbers" title="الترتيب الحالي">
                            {idx + 1}
                          </span>
                          <div className="flex flex-col">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveProduct(idx, -1)}
                              disabled={isFirst || reordering}
                              className="h-5 w-5 p-0 hover:bg-brand_blue/10"
                              data-testid={`move-up-${p.master_id || p.id}`}
                              title="نقل للأعلى"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveProduct(idx, 1)}
                              disabled={isLast || reordering}
                              className="h-5 w-5 p-0 hover:bg-brand_blue/10"
                              data-testid={`move-down-${p.master_id || p.id}`}
                              title="نقل للأسفل"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-bold">
                        {p.name}
                        {p.hidden && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-foreground/70 text-xs font-bold">
                            <EyeOff className="h-3 w-3" />
                            مخفي
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${
                            (p.kind || 'primary') === 'additional'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                          data-testid={`kind-badge-${p.master_id || p.id}`}
                        >
                          {(p.kind || 'primary') === 'additional' ? 'إضافي' : 'أساسي'}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-numbers">
                        {p.price_options_enabled && (p.price_options || []).length > 0 ? (
                          <span className="text-xs font-bold text-primary" data-testid={`price-options-badge-${p.master_id || p.id}`}>
                            {p.price_options.length} خيارات <bdi dir="ltr">({Math.min(...p.price_options.map((o) => o.price))}–{Math.max(...p.price_options.map((o) => o.price))})</bdi>
                          </span>
                        ) : (<>{p.price?.toLocaleString('en-US') || 0} <SAR /></>)}
                      </td>
                      <td className="py-3 px-4">
                        {ruleChip ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-bold">
                            <ShieldCheck className="h-3 w-3" />
                            {ruleChip.label}
                            {targeted.length + (rule ? 1 : 0) > 1 && (
                              <span className="text-[10px] opacity-70">+{targeted.length + (rule ? 1 : 0) - 1}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-foreground/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {fieldKeys.size === 0 ? (
                          <span className="text-foreground/30 text-xs">لا يوجد</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {[...fieldKeys].map((f) => (
                              <span key={f} className="px-2 py-0.5 rounded bg-destructive/10 text-destructive text-xs font-bold">
                                {FIELD_LABELS[f] || f}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${
                          (p.allowed_roles || []).length === 0
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-brand_blue/10 text-brand_blue'
                        }`} data-testid={`allowed-roles-${p.master_id || p.id}`}>
                          {renderAllowedRolesLabel(p.allowed_roles)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleHidden(p)}
                            className={`h-8 w-8 p-0 ${p.hidden ? 'text-foreground/40' : 'text-brand_blue'}`}
                            data-testid={`toggle-hidden-product-${p.master_id || p.id}`}
                            title={p.hidden ? 'إظهار المنتج' : 'إخفاء المنتج'}
                          >
                            {p.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="h-8 w-8 p-0" data-testid={`edit-product-${p.master_id || p.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} className="h-8 w-8 p-0 text-destructive" data-testid={`delete-product-${p.master_id || p.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">
              {editing ? 'تعديل المنتج' : 'إضافة منتج جديد'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="prod-name">اسم المنتج <span className="text-destructive">*</span></Label>
              <Input
                id="prod-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="مثال: حزمة شحن دولي"
                data-testid="product-name-input"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="prod-price">السعر</Label>
              <Input
                id="prod-price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0"
                data-testid="product-price-input"
                className="font-numbers"
              />
            </div>

            <PriceOptionsEditor
              enabled={form.price_options_enabled}
              options={form.price_options}
              onChange={({ enabled, options }) => setForm({ ...form, price_options_enabled: enabled, price_options: options })}
            />

            <div className="space-y-1.5">
              <Label>قاعدة الحقول الإلزامية (اختياري)</Label>
              <Select
                value={form.field_rule_id || 'none'}
                onValueChange={(v) => setForm({ ...form, field_rule_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="product-rule-select">
                  <SelectValue placeholder="بدون قاعدة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— بدون قاعدة —</SelectItem>
                  {rules.filter((r) => r.active).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <Tag className="h-3 w-3" />
                        {r.label}
                        <span className="text-xs text-foreground/50">
                          ({(r.required_fields || []).map((f) => FIELD_LABELS[f] || f).join('، ')})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-foreground/55">
                ربط مباشر إضافي. الأفضل اختيار المنتج من داخل صفحة «قواعد الحقول الإلزامية».
              </p>
            </div>

            <div className="space-y-2">
              <Label>نوع المنتج <span className="text-destructive">*</span></Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'primary', label: 'أساسي', activeCls: 'border-emerald-600 bg-emerald-50 text-emerald-800' },
                  { value: 'additional', label: 'إضافي', activeCls: 'border-purple-600 bg-purple-50 text-purple-800' },
                ].map((opt) => {
                  const active = (form.kind || 'primary') === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`product-kind-${opt.value}`}
                      onClick={() => setForm({ ...form, kind: opt.value })}
                      className={`px-4 py-1.5 rounded-md border text-sm font-bold transition-colors ${
                        active ? opt.activeCls : 'border-input bg-background text-foreground hover:bg-accent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-foreground/55">
                يظهر التصنيف في التحليل الشامل ويمكن فلترة المؤشرات على «الأساسي» أو «الإضافي».
              </p>
            </div>

            <div className="space-y-2">
              <Label>صلاحية استخدام المنتج</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, allowed_roles: [] })}
                  data-testid="role-all"
                  className={`px-3 py-1.5 rounded-md border text-sm font-bold transition-colors ${
                    (form.allowed_roles || []).length === 0
                      ? 'border-brand_blue bg-brand_blue/10 text-brand_blue'
                      : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  الجميع
                </button>
                {ROLE_OPTIONS.map((opt) => {
                  const active = (form.allowed_roles || []).includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`role-${opt.value}`}
                      onClick={() => {
                        const cur = form.allowed_roles || [];
                        const next = active ? cur.filter((r) => r !== opt.value) : [...cur, opt.value];
                        setForm({ ...form, allowed_roles: next });
                      }}
                      className={`px-3 py-1.5 rounded-md border text-sm font-bold transition-colors ${
                        active
                          ? 'border-brand_blue bg-brand_blue/10 text-brand_blue'
                          : 'border-input bg-background text-foreground hover:bg-accent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-foreground/55">
                اترك «الجميع» مفعّل لظهور المنتج لجميع الأدوار. اختر دور أو أكثر لتقييد ظهور هذا المنتج في قوائم تسجيل المبيعات.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
              <Button type="submit" disabled={saving} data-testid="save-product-button">
                {saving ? 'جاري الحفظ...' : editing ? 'حفظ التعديلات' : 'إنشاء المنتج'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sales export dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-white max-w-md" dir="rtl" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="text-right flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
              تصدير تقرير المبيعات
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>المنتج</Label>
              <Select value={exportProduct} onValueChange={setExportProduct}>
                <SelectTrigger data-testid="export-product-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— جميع المنتجات —</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={`exp-${p.master_id || p.id}`} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>الفترة</Label>
              <Select value={exportPeriod} onValueChange={setExportPeriod}>
                <SelectTrigger data-testid="export-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">اليومية</SelectItem>
                  <SelectItem value="weekly">الأسبوعية</SelectItem>
                  <SelectItem value="monthly">الشهرية</SelectItem>
                  <SelectItem value="quarterly">الربعية</SelectItem>
                  <SelectItem value="custom">نطاق مخصص</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportPeriod === 'custom' && (
              <div className="grid grid-cols-2 gap-3" data-testid="export-custom-range">
                <div className="space-y-1.5">
                  <Label>من تاريخ</Label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    data-testid="export-start-date"
                    className="text-right"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>إلى تاريخ</Label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    data-testid="export-end-date"
                    className="text-right"
                  />
                </div>
              </div>
            )}

            <div className="text-xs p-3 rounded-md bg-brand_blue/5 border border-brand_blue/20 text-foreground/75 leading-relaxed">
              يحوي الملف لكل عملية بيع: التاريخ، الفرع، الموظف، المنتج، الكمية، السعر، الإجمالي،
              <span className="font-bold"> الدولة، اسم العميل، رقم التواصل، نوع النشاط</span>،
              وحالة كود الخصم. الحقول التي لم يُسجّلها الموظف تظهر بـ «—».
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setExportOpen(false)}>إلغاء</Button>
              <Button onClick={handleExportSales} disabled={downloading} className="gap-2" data-testid="confirm-export-button">
                <Download className="h-4 w-4" />
                {downloading ? 'جاري التنزيل...' : 'تنزيل Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
